/**
 * Home page banners (a carousel edited in Admin › Banners). The same validator runs in the admin
 * form and in the API, so both agree on what a banner needs.
 */
import type { Localized } from "./types";

export const BANNER_KINDS = ["cards", "presale", "promo", "set", "image"] as const;
export type BannerKind = (typeof BANNER_KINDS)[number];

export const BANNER_THEMES = ["violet", "blue", "red", "gold", "green", "dark"] as const;
export type BannerTheme = (typeof BANNER_THEMES)[number];

export const CARD_SOURCES = ["priciest", "newest", "manual"] as const;
export type CardSource = (typeof CARD_SOURCES)[number];

/** Products a banner can show (cards side by side). */
export const BANNER_MAX_PRODUCTS = 3;
export const BANNER_MAX = 12;

export const BANNER_KIND_LABEL: Record<BannerKind, string> = {
  cards: "Cartas destacadas",
  presale: "Preventa",
  promo: "Promoción",
  set: "Expansión",
  image: "Imagen libre",
};

export const BANNER_THEME_LABEL: Record<BannerTheme, string> = {
  violet: "Violeta",
  blue: "Azul",
  red: "Rojo",
  gold: "Dorado",
  green: "Verde",
  dark: "Oscuro",
};

export interface BannerData {
  /** cards: where the three cards come from. */
  source?: CardSource;
  /** cards (manual), presale, promo: chosen products, in order. */
  productIds?: number[];
  /** promo: a short badge, e.g. "-20%" or "2x1". */
  badge?: Localized;
  /** set: the expansion's name as stored in the catalog. */
  setName?: string;
  /** image (required) and promo (optional): an uploaded image key. */
  imageKey?: string;
  /** image: alt text. */
  alt?: Localized;
  /** Every kind but image (optional): a background photo under the theme colour. */
  bgImageKey?: string;
}

/** A banner as stored and edited. */
export interface BannerInput {
  kind: BannerKind;
  active: boolean;
  startsAt: string | null;
  endsAt: string | null;
  title: Localized;
  text: Localized;
  cta: Localized;
  link: string;
  theme: BannerTheme;
  data: BannerData;
}

export interface Banner extends BannerInput {
  id: number;
  position: number;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** A product as a banner shows it. */
export interface BannerProduct {
  id: number;
  slug: string;
  name: Localized;
  imageKey: string;
  price: number;
  compareAtPrice: number | null;
  stock: number;
  presale: boolean;
  releaseDate: string | null;
}

/** GET /api/banners: what the home page renders, products resolved. */
export interface PublicBanner {
  id: number;
  kind: BannerKind;
  theme: BannerTheme;
  title: Localized;
  text: Localized;
  cta: Localized;
  link: string;
  badge: Localized | null;
  imageKey: string | null;
  alt: Localized | null;
  /** Background photo (null: just the theme colour). */
  bgImageKey: string | null;
  setName: string | null;
  /** Presale: the release date of its first product. */
  releaseDate: string | null;
  products: BannerProduct[];
}

type Parsed = { ok: true; value: BannerInput } | { ok: false; errors: string[] };

const has = (o: Record<string, unknown>, k: string) => Object.prototype.hasOwnProperty.call(o, k);
const text = (v: unknown, max: number) => (typeof v === "string" ? v.trim().replace(/\s+/g, " ").slice(0, max) : "");

function localized(v: unknown, max: number): Localized {
  const o = (v ?? {}) as Record<string, unknown>;
  return { es: text(o.es, max), en: text(o.en, max) };
}

/**
 * Site paths only (or https links): never javascript:, protocol-relative URLs or a backslash
 * (browsers read "/\evil.com" as "//evil.com").
 */
export function isSafeLink(link: string): boolean {
  if (link === "") return true;
  if (link.includes("\\")) return false;
  if (/^\/(?!\/)[^\s]*$/.test(link)) return true;
  try {
    return new URL(link).protocol === "https:";
  } catch {
    return false;
  }
}

/** Upload keys made by POST /api/admin/images (uploads/<year>/<uuid>.<ext>). */
const UPLOAD_KEY = /^uploads\/\d{4}\/[0-9a-f-]{36}\.(jpg|png|webp)$/;

function parseDate(v: unknown): { ok: boolean; value: string | null } {
  if (v === null || v === undefined || v === "") return { ok: true, value: null };
  if (typeof v !== "string") return { ok: false, value: null };
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? { ok: false, value: null } : { ok: true, value: d.toISOString() };
}

function parseIds(v: unknown): number[] | null {
  if (v === undefined || v === null) return [];
  if (!Array.isArray(v)) return null;
  const ids = v.filter((n): n is number => Number.isInteger(n) && (n as number) > 0);
  if (ids.length !== v.length) return null;
  return [...new Set(ids)].slice(0, BANNER_MAX_PRODUCTS);
}

/** Validates an untrusted banner (admin form or API body). Kind-specific fields others don't use are dropped. */
export function parseBanner(raw: unknown): Parsed {
  const b = (raw ?? {}) as Record<string, unknown>;
  const errors: string[] = [];
  const kind = (BANNER_KINDS as readonly string[]).includes(b.kind as string) ? (b.kind as BannerKind) : null;
  if (!kind) errors.push("kind");
  const theme = (BANNER_THEMES as readonly string[]).includes(b.theme as string) ? (b.theme as BannerTheme) : null;
  if (!theme) errors.push("theme");
  const starts = parseDate(b.startsAt);
  const ends = parseDate(b.endsAt);
  if (!starts.ok) errors.push("startsAt");
  if (!ends.ok) errors.push("endsAt");
  if (starts.value && ends.value && starts.value >= ends.value) errors.push("endsAt (must be after startsAt)");
  const link = text(b.link, 300);
  if (!isSafeLink(link)) errors.push("link");
  const title = localized(b.title, 120);
  const body = localized(b.text, 300);
  const cta = localized(b.cta, 40);

  const d = (b.data ?? {}) as Record<string, unknown>;
  const data: BannerData = {};
  const ids = parseIds(d.productIds);
  if (ids === null) errors.push("productIds");
  const image = typeof d.imageKey === "string" && d.imageKey ? d.imageKey : "";
  if (image && !UPLOAD_KEY.test(image)) errors.push("imageKey");
  const bg = typeof d.bgImageKey === "string" && d.bgImageKey ? d.bgImageKey : "";
  if (bg && !UPLOAD_KEY.test(bg)) errors.push("bgImageKey");

  if (kind === "cards") {
    data.source = (CARD_SOURCES as readonly string[]).includes(d.source as string) ? (d.source as CardSource) : "priciest";
    data.productIds = ids ?? [];
    if (data.source === "manual" && !data.productIds.length) errors.push("productIds (choose the cards)");
  } else if (kind === "presale") {
    data.productIds = ids ?? [];
  } else if (kind === "promo") {
    data.productIds = ids ?? [];
    data.badge = localized(d.badge, 16);
    if (image) data.imageKey = image;
  } else if (kind === "set") {
    data.setName = text(d.setName, 120);
    if (!data.setName) errors.push("setName");
  } else if (kind === "image") {
    if (!image) errors.push("imageKey (upload the image)");
    data.imageKey = image;
    data.alt = localized(d.alt, 160);
  }

  if (kind && kind !== "image" && bg) data.bgImageKey = bg;

  // Everything but an image banner needs a title to make sense.
  if (kind && kind !== "image" && !title.es) errors.push("title.es");
  if (cta.es && !link) errors.push("link (the button needs a link)");

  if (errors.length || !kind || !theme) return { ok: false, errors };
  return {
    ok: true,
    value: {
      kind,
      active: has(b, "active") ? b.active === true : true,
      startsAt: starts.value,
      endsAt: ends.value,
      title,
      text: body,
      cta,
      link,
      theme,
      data,
    },
  };
}

/** Is it shown right now? (active and inside its schedule) */
export function isLive(b: Pick<BannerInput, "active" | "startsAt" | "endsAt">, now: Date = new Date()): boolean {
  if (!b.active) return false;
  if (b.startsAt && new Date(b.startsAt) > now) return false;
  if (b.endsAt && new Date(b.endsAt) <= now) return false;
  return true;
}

/** A Spanish/English text falls back to Spanish when the English one is empty. */
export function pickText(l: Localized | null | undefined, locale: "es" | "en"): string {
  if (!l) return "";
  return (locale === "en" ? l.en || l.es : l.es) ?? "";
}

/** "2026-11-14" → "14/11" (es) or "Nov 14" (en). */
export function formatReleaseDate(date: string | null, locale: "es" | "en"): string {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return "";
  const [y, m, d] = date.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(locale === "en" ? "en-US" : "es-AR", { day: "numeric", month: "long", timeZone: "UTC" });
}
